/**
 * Pure helpers for the withdrawal review screen, kept out of the component so
 * they are testable without a DOM — the convention across this codebase.
 */
import type { WithdrawalChecklist } from '../../types/finance';

type PlaceSignal = Pick<
  WithdrawalChecklist,
  | 'location_state'
  | 'location_km_from_nearest'
  | 'location_history_points'
  | 'location_usual_range_km'
>;

/**
 * The location line, in words rather than coordinates.
 *
 * A manager decides with this, so it has to say what it means. It never says
 * where the client actually was: the coordinates are encrypted, coarsened away
 * after 90 days, and none of a reviewer's business. The only question answered
 * is whether this is a place this client has acted from before.
 *
 * And the distance is never given alone. On its own a kilometre count decides
 * nothing — 460 km is another world for a client who has never left one city
 * and a Tuesday for one who rotates between three — so the line carries that
 * client's own usual range beside it, and the manager judges the gap.
 */
export function describePlace(checklist: PlaceSignal): string {
  if (checklist.location_state === 'no_history') {
    return checklist.location_history_points === 0
      ? 'No location recorded for this request'
      : 'Not enough history to compare';
  }
  if (checklist.location_state === 'usual') return 'A place this client has used before';
  const km = checklist.location_km_from_nearest;
  if (km === null) return 'Somewhere new for this client';
  const from = `About ${Math.round(km)} km from anywhere this client has used before`;
  const usual = checklist.location_usual_range_km;
  return usual === null
    ? from
    : `${from}, and they usually stay within about ${Math.round(usual)} km`;
}

/** Warn only on a genuine deviation. 'no_history' is not a finding. */
export function placeTone(checklist: PlaceSignal): 'warn' | 'muted' {
  return checklist.location_state === 'unusual' ? 'warn' : 'muted';
}

type EdgeSignal = Pick<
  WithdrawalChecklist,
  | 'geo_edge_state'
  | 'geo_edge_country'
  | 'geo_declared_country'
  | 'geo_edge_times_seen_before'
  | 'geo_edge_needs_a_look'
>;

/**
 * The network's line, next to the browser's.
 *
 * `describePlace` above rests on a reading the client's own browser volunteers,
 * which is spoofable. This one rests on the country the load balancer resolved from
 * the connection, which the client cannot set. Different source, same question.
 *
 * The wording never leaves "abroad" on its own. A client who has been to that
 * country before reads as exactly that, because otherwise a Mexican client who
 * summers in Spain would produce a finding on every request and the manager would
 * learn to skip the line — the failure the unclaimed-money alert had before it
 * became a queue.
 *
 * Countries are ISO-2 codes on purpose. Translating them to names here would need a
 * country list in the bundle, and "ES" is what the reviewer can check against the
 * rest of the file.
 */
export function describeEdgeCountry(checklist: EdgeSignal): string {
  const seen = checklist.geo_edge_country;
  const declared = checklist.geo_declared_country;
  switch (checklist.geo_edge_state) {
    case 'no_signal':
      return 'No connection country recorded for this request';
    case 'home':
      return `Connected from ${seen}, where they say they live`;
    case 'unknown_residence':
      return `Connected from ${seen}; no declared country on file to compare with`;
    case 'travel_repeat': {
      const times = checklist.geo_edge_times_seen_before;
      const before = times === 1 ? 'once before' : `${times} times before`;
      return `Connected from ${seen}, not ${declared} — but they have done that ${before}`;
    }
    case 'travel_first':
      return `Connected from ${seen}, not ${declared}, and never from there before`;
    default:
      // A state this build does not know (or a payload without the field, mid-deploy):
      // absent data reads as absent, never as a finding and never as reassurance.
      return 'Connection signal unavailable';
  }
}

/**
 * The backend decides which state is worth interrupting a reviewer for
 * (`travel_first` today) and sends the verdict as `geo_edge_needs_a_look` — the
 * SPA renders it rather than re-deriving the rule, so a recalibration on the
 * server cannot silently disagree with the colour on screen. Strict `=== true`:
 * a missing field mutes rather than accuses.
 */
export function edgeCountryTone(checklist: EdgeSignal): 'warn' | 'muted' {
  return checklist.geo_edge_needs_a_look === true ? 'warn' : 'muted';
}

type TakeoverSignal = Pick<
  WithdrawalChecklist,
  'takeover_state' | 'takeover_detail' | 'takeover_history' | 'takeover_needs_a_look'
>;

/**
 * The sequence line.
 *
 * The backend already phrases the finding for a reviewer, so this does not re-word it —
 * re-wording it here would mean two places to keep in step and one of them would drift.
 * What this adds is the two non-findings, which have to read differently from each other:
 * "we do not know this client yet" is not "we know them and nothing happened", and a
 * reviewer who cannot tell those apart cannot weigh the silence.
 */
export function describeTakeover(checklist: TakeoverSignal): string {
  if (checklist.takeover_state === 'not_enough_history') {
    return `Not enough sign-in history yet to judge (${checklist.takeover_history} events)`;
  }
  if (checklist.takeover_state === 'nothing_seen') return 'Nothing unusual in recent sign-ins';
  // A sequence state always ships with the backend's reviewer-facing wording. A payload
  // without one (a state this build does not know, or the field missing mid-deploy) is
  // absent data — it must never read as an accusation the backend did not make.
  return checklist.takeover_detail ?? 'Sign-in data unavailable for this request';
}

/**
 * The backend's verdict (`takeover_needs_a_look`), rendered rather than re-derived —
 * same reason as `edgeCountryTone`. Strict `=== true`: a missing field mutes.
 */
export function takeoverTone(checklist: TakeoverSignal): 'warn' | 'muted' {
  return checklist.takeover_needs_a_look === true ? 'warn' : 'muted';
}
